import { useCallback, useState, useEffect } from 'react';

import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { createContainer, useContainer } from 'unstated-next';

import { GET_USER_DATA } from '../../../../../config/apiPaths';
import { localStorageKeys } from '../../../../../localStorageKeys';
import { getUserUuidHeaders } from '../../../../../utils/httpUtils';
import { getUrlParams, getUrls, processApiPath } from '../../../../../utils/url';
import AdminAuthContainer from '../../../AdminAuthContainer';

interface UserContainerState {
    userData?: Record<string, any>; // result of the GET_CLASS_DATA_PATH api call
}

type LoadingStatus = 'INITIAL' | 'LOADING' | 'SUCCESS' | 'ERROR';

const useUserContainer = (state: UserContainerState = {}) => {
    console.log('UserContainer');
    const { api } = getUrls();

    const [userData, setUserData] = useState<any>();

    const { t: tCommon } = useTranslation('common');

    const { authMode, showDemoContent } = useContainer(AdminAuthContainer);

    const search = new URLSearchParams(window.location.search);
    const userUuid = search.get('user_uuid') || window.localStorage.getItem(localStorageKeys.userUuid);

    const [getUserStatus, setGetUserStatus] = useState<LoadingStatus>('INITIAL');
    const [errorMessage, setErrorMessage] = useState<string>('');

    const { userId: userIdV1 } = useParams<{ userId?: string }>();
    const { user_id: userIdV2 } = getUrlParams();
    const userId = userIdV1 || userIdV2 || '';

    const returnErrorMessage = useCallback(
        (error: any): string => {
            console.error(error);
            return tCommon('errorMessage');
        },
        [tCommon]
    );

    const getUserDataCall = useCallback(async () => {
        const { data: responseData } = await axios.get(
            processApiPath(GET_USER_DATA, undefined, {
                user_id: userId,
                auth_mode: authMode,
                demo_content: String(showDemoContent)
            }),
            { headers: getUserUuidHeaders(userUuid as string) }
        );
        const { data } = responseData.response_too_large_url
            ? await axios.get(responseData.response_too_large_url)
            : { data: responseData };

        if (data?.success === false) {
            throw Error(data?.error);
        }
        return data;
    }, [userUuid, userId, authMode, showDemoContent]);

    const getUser = useCallback(async () => {
        setGetUserStatus('LOADING');

        try {
            const data = state.userData ?? (await getUserDataCall());

            setUserData(data);
            setGetUserStatus('SUCCESS');
        } catch (err) {
            const errorMsg = returnErrorMessage(err);
            setErrorMessage(errorMsg);
            setGetUserStatus('ERROR');
        }
    }, [state.userData, getUserDataCall, returnErrorMessage]);

    useEffect(() => {
        if (userId && userUuid) {
            getUser();
        }
    }, [userId, getUser, userUuid]);

    return {
        userId,
        api,
        userData,
        getUserStatus,
        errorMessage
    };
};

const UserContainer = createContainer(useUserContainer);

export default UserContainer;
